import React, { useEffect, useMemo, useState } from "react";
import { FcGoogle } from "react-icons/fc";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { signUpWithGoogle } from "../utils/firebase.utils";
import { Formik, Form } from "formik";
import {
  feildsLogin,
  initialValuesLogin,
  validationSchemaLogin,
} from "../fromsUtil/login.form";
import { logOut, login, loginGoogle } from "../logic/login/action";
import { useDispatch, useSelector } from "react-redux";
import logo from "../images/logo_clock.png";
import { errorMessage } from "../logic/error/action";
import {
  CircularProgress,
  Button,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import { failedAuth, loadingAuth } from "../logic/login/reducer";
import { motion } from "framer-motion";
import ThreeBackground from "../components/ThreeBackground";
import ModernInput from "../components/ModernInput";
import { MdArrowForward, MdLock, MdEmail } from "react-icons/md";

const Login = () => {
  const route = useLocation();
  const nav = useNavigate();
  const dispatch = useDispatch();
  const selectorAuth = useSelector((state) => state.auth);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const superadmin = useMemo(() => {
    const dt = new URLSearchParams(route.search);
    if (dt.get("superadmin")) {
      return true;
    } else {
      return false;
    }
  }, [route.search]);

  const [type, settype] = useState("business");

  useEffect(() => {
    if (superadmin && type !== "admin") {
      settype("admin");
    }
  }, [superadmin, type]);

  const onSubmit = async (values, submitProps) => {
    setIsSubmitting(true);
    await login(dispatch, values, type, nav);
    submitProps.setSubmitting(false);
    setIsSubmitting(false);
    submitProps.resetForm();
  };

  const loginWithGoogle = async () => {
    try {
      dispatch(loadingAuth());
      const googleData = await signUpWithGoogle();
      if (googleData) {
        const req = {
          email: googleData.email,
          google_id: googleData.google_id,
          access_token: googleData.access_token,
          google_headers: JSON.stringify({
            Authorization: `Bearer ${googleData.access_token}`,
          }),
          fcm: "fcm",
        };
        await loginGoogle(dispatch, req, nav);
      } else {
        dispatch(failedAuth({ message: "Please select account" }));
        errorMessage(dispatch, "Please select account");
      }
    } catch (error) {
      console.log(error);
      dispatch(failedAuth({ message: "Error signing in with Google" }));
      errorMessage(dispatch, "Error signing in with Google");
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const benefits = [
    "Access your dashboard instantly",
    "Manage all your appointments",
    "Track your business growth",
    "Secure and reliable platform",
  ];

  return (
    <div className="w-screen h-screen bg-gradient-to-br from-[#667eea] via-[#764ba2] to-[#0B469D] relative overflow-hidden flex flex-col">
      <ThreeBackground />

      {/* Loading Overlay */}
      {selectorAuth.status === "loading" && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            className="bg-white rounded-2xl p-6 shadow-2xl mx-4"
          >
            <CircularProgress size={60} sx={{ color: "#0B469D" }} />
            <Typography className="mt-4 text-gray-700 text-sm">
              Signing you in...
            </Typography>
          </motion.div>
        </motion.div>
      )}

      {/* Header */}
      <motion.header
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="w-full flex items-center py-2 px-3 sm:px-4 md:px-6 lg:px-8 flex-shrink-0 relative z-10"
      >
        <motion.a
          href="/"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="flex-shrink-0"
        >
          <img
            src={logo}
            className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 drop-shadow-lg"
            alt="Logo"
          />
        </motion.a>
        <div className="flex items-center gap-2 ml-auto">
          <motion.p
            whileHover={{ scale: 1.05 }}
            className="hidden md:inline-block text-white/90 hover:text-white transition-colors text-xs sm:text-sm"
          >
            Need an account?
          </motion.p>
          <Link to={"/admin/register"} onClick={() => logOut(dispatch)}>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                variant="outlined"
                size="small"
                sx={{
                  color: "white",
                  borderColor: "rgba(255, 255, 255, 0.5)",
                  borderRadius: "8px",
                  px: { xs: 1.5, sm: 2 },
                  py: 0.5,
                  fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.875rem" },
                  "&:hover": {
                    borderColor: "white",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                Sign Up
              </Button>
            </motion.div>
          </Link>
        </div>
      </motion.header>

      {/* Main Content */}
      <div className="w-full flex-1 flex items-center px-2 sm:px-3 md:px-4 lg:px-6 relative z-10 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6 lg:gap-8 items-center w-full h-full max-w-7xl mx-auto">
          {/* Left Side - Content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="hidden lg:flex flex-col gap-2 sm:gap-3 text-white h-full justify-center"
          >
            <motion.div variants={itemVariants}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: "2.5rem", xl: "3rem" },
                  fontWeight: "bold",
                  lineHeight: 1.2,
                  mb: 2,
                  background:
                    "linear-gradient(135deg, #ffffff 0%, #e0e7ff 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Welcome Back
                <span className="text-[#72a9fb]">.</span>
              </Typography>
            </motion.div>

            <motion.p
              variants={itemVariants}
              sx={{
                fontSize: { lg: "1rem", xl: "1.125rem" },
                lineHeight: 1.5,
                color: "rgba(255, 255, 255, 0.9)",
                mb: 2,
              }}
            >
              Sign in to continue managing your business, appointments, and grow
              your success.
            </motion.p>

            <motion.ul variants={itemVariants} className="space-y-1.5">
              {benefits.map((benefit, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="flex items-center gap-2 text-white/90 text-sm lg:text-base"
                >
                  <MdLock className="text-[#72a9fb] flex-shrink-0 text-lg" />
                  <span>{benefit}</span>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>

          {/* Right Side - Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex justify-center w-full h-full items-center"
          >
            <motion.div
              transition={{ duration: 0.3 }}
              className="w-full max-w-md lg:max-w-lg h-full flex items-center"
            >
              <Box
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.98)",
                  backdropFilter: "blur(20px)",
                  borderRadius: "20px",
                  padding: { xs: 2.5, sm: 3, md: 3.5 },
                  boxShadow:
                    "0 25px 70px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.5)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  height: { xs: "100%", lg: "auto" },
                  maxHeight: "100%",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="flex flex-col h-full"
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontSize: {
                        xs: "1.375rem",
                        sm: "1.625rem",
                        md: "1.875rem",
                      },
                      fontWeight: 700,
                      color: "#111827",
                      mb: 1,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    Login
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: {
                        xs: "0.75rem",
                        sm: "0.8125rem",
                        md: "0.875rem",
                      },
                      color: "#4b5563",
                      mb: { xs: 2, sm: 2.5 },
                    }}
                  >
                    Sign in to your account
                  </Typography>

                  {/* Form */}
                  <Formik
                    initialValues={initialValuesLogin}
                    validationSchema={validationSchemaLogin}
                    onSubmit={onSubmit}
                  >
                    {({ isSubmitting: formSubmitting, isValid }) => (
                      <Form className="flex flex-col flex-1 min-h-0">
                        <div className="flex flex-col gap-3 sm:gap-4 flex-1">
                          {feildsLogin.map((field, index) => (
                            <motion.div
                              key={field.name}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.4 + index * 0.05 }}
                            >
                              <ModernInput comp={field} />
                            </motion.div>
                          ))}

                          <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5 }}
                            className="mt-1"
                          >
                            <Link
                              to="/admin/verify/forgot-password"
                              className="text-[#0B469D] hover:text-[#72a9fb] text-xs sm:text-sm font-medium transition-colors float-right"
                            >
                              Forgot password?
                            </Link>
                          </motion.div>

                          <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6 }}
                            className="mt-auto pt-2 flex-shrink-0"
                          >
                            <motion.div
                              whileHover={{ scale: 1.01 }}
                              whileTap={{ scale: 0.99 }}
                            >
                              <Button
                                type="submit"
                                disabled={
                                  formSubmitting || isSubmitting || !isValid
                                }
                                fullWidth
                                variant="contained"
                                size="small"
                                endIcon={<MdArrowForward size={18} />}
                                sx={{
                                  borderRadius: "10px",
                                  py: { xs: 0.875, sm: 1.125 },
                                  background:
                                    "linear-gradient(135deg, #0B469D 0%, #72a9fb 100%)",
                                  textTransform: "none",
                                  fontSize: {
                                    xs: "0.8125rem",
                                    sm: "0.875rem",
                                  },
                                  fontWeight: 600,
                                  boxShadow:
                                    "0 6px 20px rgba(11, 70, 157, 0.35), 0 2px 8px rgba(11, 70, 157, 0.2)",
                                  "&:hover": {
                                    background:
                                      "linear-gradient(135deg, #0a3d7a 0%, #5a8fd8 100%)",
                                    boxShadow:
                                      "0 8px 25px rgba(11, 70, 157, 0.45), 0 4px 12px rgba(11, 70, 157, 0.25)",
                                    transform: "translateY(-1px)",
                                  },
                                  "&:disabled": {
                                    background:
                                      "linear-gradient(135deg, #0B469D 0%, #72a9fb 100%)",
                                    opacity: 0.6,
                                  },
                                }}
                              >
                                {formSubmitting || isSubmitting
                                  ? "Signing in..."
                                  : "Sign In"}
                              </Button>
                            </motion.div>
                          </motion.div>
                        </div>
                      </Form>
                    )}
                  </Formik>

                  <div className="pt-3 border-t border-gray-200 mt-3 flex-shrink-0 space-y-3">
                    {/* Google Sign In Button */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 1 }}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                    >
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={loginWithGoogle}
                        size="small"
                        startIcon={<FcGoogle size={20} />}
                        sx={{
                          borderRadius: "10px",
                          py: { xs: 0.875, sm: 1 },
                          borderColor: "#d1d5db",
                          color: "#374151",
                          textTransform: "none",
                          fontSize: {
                            xs: "0.8125rem",
                            sm: "0.875rem",
                          },
                          fontWeight: 500,
                          backgroundColor: "white",
                          "&:hover": {
                            borderColor: "#0B469D",
                            backgroundColor: "rgba(11, 70, 157, 0.05)",
                            color: "#0B469D",
                          },
                        }}
                      >
                        Continue with Google
                      </Button>
                    </motion.div>
                    <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#6b7280' }}>
                      Platform admin?{' '}
                      <Link to="/admin/login/admin" className="text-[#0B469D] font-medium">
                        Super admin login
                      </Link>
                    </Typography>
                  </div>
                </motion.div>
              </Box>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Login;
